import { AlertCircle, ArrowLeft, Check, Copy, Download, LoaderCircle, Mail, MapPin, Phone, Share2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { DigitalCardView } from '../components/DigitalCardView'
import { Footer, Header } from '../components/SiteChrome'
import { SocialLinks } from '../components/SocialLinks'
import { downloadVCard, fullPhone, safeExternalUrl } from '../lib/card'
import { getPublicCard } from '../lib/supabase'
import type { DigitalCard } from '../types/card'

export function CardPage({ cardId }: { cardId: string }) {
  const [card, setCard] = useState<DigitalCard | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let current = true
    setError('')
    setCard(null)
    getPublicCard(cardId).then((result) => { if (current) setCard(result) }).catch((reason: unknown) => {
      if (current) setError(reason instanceof Error ? reason.message : 'We could not open this card.')
    })
    return () => { current = false }
  }, [cardId])

  useEffect(() => {
    if (!card) return
    document.title = `${card.name} — Sandhi Card`
    const description = card.introduction || `${card.name}'s digital business card`
    document.querySelector('meta[name="description"]')?.setAttribute('content', description)
    return () => { document.title = 'Sandhi Card — One card. Every connection.' }
  }, [card])

  const customFields = useMemo(() => Object.entries(card?.custom_fields || {}).filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value)), [card])

  async function shareCard() {
    if (!card) return
    const shareData = { title: `${card.name} — Sandhi Card`, text: `Connect with ${card.name}`, url: window.location.href }
    if (navigator.share) await navigator.share(shareData).catch(() => undefined)
    else {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    }
  }

  return (
    <div className="card-page">
      <Header compact />
      <main className="card-page__main">
        {!card && !error && <div className="state-card"><LoaderCircle className="spin" size={32} /><h1>Opening card…</h1><p>Fetching the latest details.</p></div>}
        {error && <div className="state-card state-card--error"><AlertCircle size={36} /><h1>We couldn’t show this card</h1><p>{error}</p><a className="button button--dark" href="/"><ArrowLeft size={17} /> Go to Sandhi</a></div>}
        {card && <>
          <div className="card-detail-layout">
            <div className="card-detail-sticky">
              <div className="card-detail-label"><span /> DIGITAL BUSINESS CARD</div>
              <DigitalCardView card={card} />
              <div className="card-primary-actions">
                <button className="button button--primary" onClick={() => downloadVCard(card)}><Download size={18} /> Save contact</button>
                <button className="button button--quiet" onClick={shareCard}>{copied ? <Check size={18} /> : <Share2 size={18} />}{copied ? 'Link copied' : 'Share'}</button>
              </div>
              <p className="safe-note"><Check size={13} /> No app needed. Saves as a standard contact file.</p>
            </div>
            <div className="card-detail-content">
              <div className="card-intro-block"><p>Nice to meet you</p><h1>{card.name}</h1><h2>{card.job_title || card.profession}{card.company ? ` at ${card.company}` : ''}</h2>{card.introduction && <p className="lead">{card.introduction}</p>}</div>
              <section className="card-detail-panel contact-panel">
                <div className="section-kicker">Contact</div>
                <div className="detail-links">
                  {card.email && <DetailLink icon={<Mail />} label="Email" value={card.email} href={`mailto:${card.email}`} />}
                  {card.phone_number && <DetailLink icon={<Phone />} label="Phone" value={`${card.country_code} ${card.phone_number}`} href={`tel:${fullPhone(card)}`} />}
                  {(card.full_address || card.location_url) && <DetailLink icon={<MapPin />} label="Location" value={card.full_address || 'Open location'} href={safeExternalUrl(card.location_url) || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(card.full_address || '')}`} external />}
                </div>
              </section>
              <SocialLinks card={card} />
              {customFields.length > 0 && <section className="card-detail-panel"><div className="section-kicker">More details</div><dl className="custom-fields">{customFields.map(([label, value]) => <div key={label}><dt>{label.replace(/_/g, ' ')}</dt><dd>{String(value)}</dd></div>)}</dl></section>}
              <div className="copy-link"><div><b>Keep this card handy</b><span>This link always shows the latest information.</span></div><button onClick={async () => { await navigator.clipboard.writeText(window.location.href); setCopied(true) }}><Copy size={17} />{copied ? 'Copied' : 'Copy link'}</button></div>
            </div>
          </div>
        </>}
      </main>
      <Footer />
    </div>
  )
}

function DetailLink({ icon, label, value, href, external = false }: { icon: ReactNode; label: string; value: string; href: string; external?: boolean }) {
  return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}><i>{icon}</i><span><small>{label}</small>{value}</span><span className="detail-arrow">↗</span></a>
}
